import { privateApi } from 'api'
import { AxiosResponse } from 'axios'
import {
  EServiceStatus,
  IAdminServicesResponse,
  IAdminService,
  IServiceCategory,
  ICreatePageService,
  IPageBooking,
  ICreateBookingAdmin,
  IServiceOccupancy
} from 'models/services'
import { getNormalizedFormData } from 'shared/utils/getNormalizedFormData'

// slots и existing_photos — массивы: сериализуем одной JSON-строкой, иначе formData разложит поэлементно
// photos (File[]) оставляем массивом — getNormalizedFormData добавит каждый файл под ключом photos
const buildServiceFormData = (
  data: Partial<ICreatePageService>
) => {
  const payload: Record<string, unknown> = { ...data }
  if (data.slots) payload.slots = JSON.stringify(data.slots)
  if (data.staff_emails) payload.staff_emails = JSON.stringify(data.staff_emails)
  if (data.existing_photos) payload.existing_photos = JSON.stringify(data.existing_photos)
  return getNormalizedFormData(payload)
}

export const servicesApi = {
  // page — slug лендинг-страницы; без него возвращаются категории основного сайта
  getServiceCategories: (
    page?: string
  ): Promise<AxiosResponse<IServiceCategory[]>> => {
    return privateApi.get('/services-categories', {
      params: page ? { page } : {}
    })
  },
  createServiceCategory: (data: {
    name: string
    photo: File
    page?: string
  }) => {
    const formData = getNormalizedFormData(data)
    return privateApi.post('/services-categories', formData)
  },
  // фото передаём, только если его меняли: иначе останется прежнее
  updateServiceCategory: (id: string, data: { name?: string; photo?: File }) => {
    const formData = getNormalizedFormData(data)
    return privateApi.patch(`/services-categories/${id}`, formData)
  },
  deleteServiceCategory: (id: string) => {
    return privateApi.delete('/services-categories', { params: { _id: id } })
  },
  // услуги лендинг-страницы по slug
  getPageServices: (
    page: string
  ): Promise<AxiosResponse<IAdminServicesResponse>> => {
    return privateApi.get('/admin/services', { params: { page, limit: 1000 } })
  },
  getServiceById: (id: string): Promise<AxiosResponse<IAdminService>> => {
    return privateApi.get(`/services/${id}`)
  },
  createPageService: (data: ICreatePageService) => {
    const formData = buildServiceFormData(data)
    return privateApi.post('/admin/services', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000
    })
  },
  updatePageService: (id: string, data: Partial<ICreatePageService>) => {
    const formData = buildServiceFormData(data)
    return privateApi.patch(`/admin/services/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000
    })
  },
  // смена статуса услуги, в том числе возврат из архива
  changeServiceStatus: (id: string, status: EServiceStatus) => {
    return privateApi.put(`/admin/services/change-status/${id}`, { status })
  },
  deleteService: (id: string) => {
    return privateApi.put('/admin/services/actions', null, {
      params: { toDelete: id }
    })
  },
  // записи лендинг-страницы (все статусы, ближайшие сверху)
  // archived добавляет к выборке прошедшие записи, from/to сужают её по дате
  getPageBookings: (
    page: string,
    params?: { archived?: boolean; from?: string; to?: string }
  ): Promise<AxiosResponse<IPageBooking[]>> => {
    return privateApi.get('/admin/service-booking', {
      params: {
        page,
        archived: params?.archived ? 'true' : undefined,
        from: params?.from || undefined,
        to: params?.to || undefined
      }
    })
  },
  // занятость слотов услуги (для выбора даты/времени как на вебе)
  getBookedDatetimes: (
    serviceId: string
  ): Promise<AxiosResponse<IServiceOccupancy>> => {
    return privateApi.get(`/service-booking/booked/${serviceId}`)
  },
  // ручная запись администрацией (без предоплаты)
  createBooking: (data: ICreateBookingAdmin) => {
    return privateApi.post('/admin/service-booking', data)
  },
  // отмена записи (с возвратом средств, если была оплачена)
  cancelBooking: (id: string) => {
    return privateApi.patch(`/admin/service-booking/${id}/cancel`)
  }
}
